// /////////////////////////////////////////////////////////////
// [ファイル] useSfaStructure.ts
// [概要] 推進案件機能統合（リリース1）の案件構造 API クライアントとデータフック
//        案件 1 件に仕入契約（0〜N）と販売区画（1〜N）を内包するモデル。
//        判定（タイプ・計上月・予定超過・仕入状況）はすべてバックエンドで行い、
//        ここでは取得と保存だけを受け持つ。設計正本 docs/推進案件機能統合_設計書.md
// /////////////////////////////////////////////////////////////
import { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { ep } from '../api/endpoints';
import { DEFAULT_FILTERS, ProjectFilters, SortConfig } from './useProjectTable';
import { DEFAULT_PHASES } from '../constants/masterData';

export type ProjectKind = 'purchase' | 'sales_only';
export type UnitState = 'idle' | 'open' | 'won' | 'lost';
export type BuyStatus = 'before' | 'contracted' | 'settled';

export const IDLE_PHASE = '0.未着手';
// 0.未着手 は「販売前」(仕入は進んでいるが販売はまだ) と表示し、選択肢には出さない (2026-11-02)
export const IDLE_LABEL = '販売前';
export const CONTRACT_PHASE = '5.内諾/売買契約';
export const ORDER_PHASE = '6.受注(契約)/決済';
export const LOST_PHASE = '7.失注';
// 仕入契約がすべて失注になったとき、一緒に失注にした区画の失注理由（人は選ばない）
export const PURCHASE_LOST_REASON = 'purchase_lost';
// 仕入フェーズの表示名。マスタの値は売上と共通（旧画面との同期・抽出条件・移行済みデータが同じ値を使う）のため値は変えず、
// 仕入側では「受注」「失注」という売る側の言葉を仕入の言葉に置き換えて表示する（2026-11-03 ユーザー決定。提案書の P6/P9 に合わせる）
export const PURCHASE_PHASE_LABEL: Record<string, string> = { [ORDER_PHASE]: '6.決済', [LOST_PHASE]: '7.失注（仕入断念）' };
export const purchasePhaseLabel = (phase: string | null | undefined): string => (phase ? PURCHASE_PHASE_LABEL[phase] || phase : '');
export const UNIT_PHASES =[IDLE_PHASE, '1.見込み', '2.アポイント/買付提出', '3.プレゼン/条件摺合わせ', '4.クロージング/売渡書取得', CONTRACT_PHASE, ORDER_PHASE, LOST_PHASE];
// 人が選べるフェーズ (仕入フェーズ・区画フェーズの変更、抽出条件)。仕入と売上で同じ名前を使う
export const SELECTABLE_PHASES = UNIT_PHASES.filter((p) => p !== IDLE_PHASE);
export const UNIT_KINDS = ['建売', '土地売', '注文住宅', '改修', '仲介', 'その他'];

export interface SfaProjectRow {
  project_id: string;
  project_name: string;
  land_name: string | null;
  project_kind: ProjectKind | null;
  record_type: string | null;
  category_large: string | null;
  category_medium: string | null;
  client_name: string | null;
  main_staff_name: string | null;
  main_staff_user_id: string | null;
  main_staff_branch: string | null;
  main_staff_branch_name: string | null;
  created_date: string | null;
  updated_date: string | null;
  remarks: string | null;
  system_numbers: string[] | null; // 案件番号 (契約・区画の番号の和集合)
  contract_count: number;
  unit_count: number;
  won_count: number;
  open_count: number;
  idle_count: number;
  lost_count: number;
  rep_phase: string;
  purchase_phase: string | null; // 仕入フェーズの代表 (仕入契約が無ければ null)
  judge_phase: string; // 抽出条件の判定に使うフェーズ (販売前なら仕入フェーズ、販売開始後は売上フェーズ)
  unassigned_parcel_count: number;
  active_area: number;
  purchase_total: number;
  sales_total: number;
  booking_ym: string | null;
  is_overdue: boolean | null;
  buy_status: BuyStatus | null;
  contract_overdue: boolean | null;
  settlement_date: string | null;
  expected_contract_ym: string | null;
}

export interface SfaParcel {
  id: number;
  contract_id: number;
  unit_id: number | null;
  unit_name?: string | null;
  location: string | null;
  chiban: string;
  land_category: string | null;
  land_area: number | null;
  status: 'active' | 'closed';
  created_by_revision_id: number | null;
  closed_by_revision_id: number | null;
  touki_property_id: number | null;
  touki_location?: string | null;
  touki_chiban?: string | null;
  current_owner_name?: string | null;
}

export interface SfaParcelRevision {
  id: number;
  rev_no: number;
  registered_date: string | null;
  judge: 'merge' | 'split' | 'none';
  area_before: number | null;
  area_after: number | null;
  area_diff_reason: string | null;
  before_chiban: string | null;
  after_chiban: string | null;
  before_count: number;
  after_count: number;
}

// 案件番号ミラー (v_legacy_anken) の 1 行。Hozenall NET と工事管理の案件ヘッダー
export interface LegacyAnkenRow {
  anken_no: string;          // 12 桁
  anken_no_display: string;  // 2-4-6 桁
  in_hozenallnet: boolean;
  in_koji: boolean;
  anken_name: string | null;   // 工事管理の案件名 (無ければ Hozenall NET の受注件名)
  building_name: string | null;
  property_name: string | null;
  address: string | null;
  client_name: string | null;
  owner_name: string | null;
  management_company_name: string | null;
  reception_date: string | null;
  hn_status: number | null;
  hn_status_name: string | null;
  hn_staff_name: string | null;
  kj_status: number | null;
  kj_order_date: string | null;
  kj_completion_date: string | null;
  deleted: boolean;
  source_updated_at: string | null;
  synced_at: string | null;
  status_code: number | null;   // 案件ステータス (両システム共通のコード。92 却下・99 失注)
  status_name: string | null;
  selectable: boolean;          // 登録できる番号か (12 桁・未削除・却下/失注以外)
  linked_project_id?: string | null;   // 既に紐付いている案件 (あれば選べない)
  linked_project_name?: string | null;
}

// 契約・区画に紐付いた案件番号 (trn_sfa_anken_links + ミラーの表示名。ミラーに無ければ紐付け時の写し)
export interface SfaAnkenLink {
  id: number;
  anken_no: string;
  anken_no_display: string;
  source: 'mirror' | 'manual';
  anken_name: string | null;
  building_name: string | null;
  property_name: string | null;
  client_name: string | null;
  reception_date: string | null;
  in_mirror: boolean;
  in_hozenallnet: boolean;
  in_koji: boolean;
  deleted: boolean;
  status_code: number | null;  // 元システムの案件ステータス (紐付け後に変わる。92 却下・99 失注)
  status_name: string | null;
  closed: boolean;             // 紐付け後に却下・失注になった
  linked_at: string | null;
  linked_by: string | null;
}

export interface SfaContract {
  id: number;
  project_id: string;
  contract_no: number;
  contract_name: string | null;
  seller_customer_id: string | null;
  seller_name: string | null;
  broker_customer_id: string | null;
  broker_name: string | null;
  expected_contract_ym: string | null;
  contract_date: string | null;
  settlement_date: string | null;
  estimated_amount: number | null;
  contract_amount: number | null;
  brokerage_fee: number | null;
  cost_basis: 'area' | 'price';
  memo: string | null;
  system_numbers: string[] | null; // 案件番号 (12 桁の保存形。紐付け anken_links の控え)
  anken_links: SfaAnkenLink[]; // 案件番号の紐付け (ミラーの案件名などを添える)
  phase: string; // 仕入フェーズ
  lost_reason_category: string | null;
  lost_reason_detail: string | null;
  buy_status: BuyStatus;
  contract_overdue: boolean;
  active_area: number;
  active_parcel_count: number;
  original_parcel_count: number;
  parcels: SfaParcel[];
  revisions: SfaParcelRevision[];
}

export interface SfaUnitCustomer {
  id?: number;
  customer_id: string;
  display_name?: string;
  customer_type?: string;
  share_numerator?: number | null;
  share_denominator?: number | null;
  is_representative?: boolean;
}

export interface SfaUnit {
  id: number;
  project_id: string;
  unit_no: number;
  unit_name: string | null;
  unit_kind: string | null;
  phase: string;
  expected_order_ym: string | null;
  order_confirmed_date: string | null;
  sales_amount: number | null;
  construction_cost: number | null;
  brokerage_fee_in: number | null;
  brokerage_fee_out: number | null;
  broker_customer_id: string | null;
  broker_name: string | null;
  system_numbers: string[] | null; // 案件番号 (12 桁の保存形。紐付け anken_links の控え)
  anken_links: SfaAnkenLink[]; // 案件番号の紐付け (ミラーの案件名などを添える)
  lost_reason_category: string | null;
  lost_reason_detail: string | null;
  unit_state: UnitState;
  booking_ym: string | null;
  is_overdue: boolean;
  plan_diff_months: number | null;
  rank_code: string | null;
  probability: number | null;
  parcel_chiban: string | null;
  parcel_area: number;
  customers: SfaUnitCustomer[];
}

export interface SfaUnitHistory {
  history_id: number;
  unit_id: number;
  unit_name: string | null;
  change_kind: 'phase' | 'expected_ym' | 'lost_all';
  phase_before: string | null;
  phase_after: string | null;
  expected_ym_before: string | null;
  expected_ym_after: string | null;
  lost_reason_category: string | null;
  lost_reason_detail: string | null;
  amount: number | null;
  changed_by: string | null;
  changed_at: string;
}

export interface SfaContractHistory {
  history_id: number;
  contract_id: number;
  contract_name: string | null;
  change_kind: 'phase' | 'legacy' | 'lost_all'; // legacy = 旧画面での変更を同期
  phase_before: string | null;
  phase_after: string | null;
  lost_reason_category: string | null;
  lost_reason_detail: string | null;
  changed_by: string | null;
  changed_at: string;
}

export interface SfaProjectDetail {
  project: any;
  contracts: SfaContract[];
  units: SfaUnit[];
  phase_history: SfaUnitHistory[];
  contract_phase_history: SfaContractHistory[];
  allocation: { unit_id: number; unit_name: string | null; allocated_cost: number; gross_profit: number }[];
}

// 一覧の抽出条件 = 旧画面の「抽出条件」(ProjectFilters) + 統合版固有の項目。
// 保存済み条件 (案件条件リスト) をそのまま適用できるよう旧キーを維持する
export interface SfaListFilters extends ProjectFilters {
  project_kind?: '' | ProjectKind;
  search_word?: string;
  branch?: string;
  staff_id?: string;
  exclude_lost?: boolean;
}

// 既定は旧画面と同じ (受注・失注を除く)。販売前の仕入案件は仕入フェーズで判定されるため「0.未着手」は足さない
export const DEFAULT_LIST_FILTERS: SfaListFilters = {
  ...DEFAULT_FILTERS,
  phase: [...DEFAULT_PHASES],
  project_kind: '',
  search_word: '',
};
export const DEFAULT_LIST_SORT: SortConfig = { field: 'updated_date', order: 'desc' };

// 一覧の状態 (抽出条件・並び順・ページ・表示件数・適用中の条件名) をタブ内で覚えておく保存先。
// sessionStorage なのでタブを閉じると消える。ログインユーザーごとにキーを分ける (形を変えたら版を上げる)
export const listStateKey = (email: string) => `sfa:projects:list:v1:${email}`;
// 一覧のスクロール位置もタブ内に覚え、HOME 経由・再読み込みでも同じ位置から表示する (変化が多いので状態とは別キー)
export const listScrollKey = (email: string) => `${listStateKey(email)}:scroll`;

export function loadListScroll(key?: string): number {
  if (!key) return 0;
  try {
    const v = Number(sessionStorage.getItem(key));
    return v > 0 ? v : 0;
  } catch {
    return 0;
  }
}

export function saveListScroll(key: string | undefined, top: number): void {
  if (!key) return;
  try {
    sessionStorage.setItem(key, String(Math.max(0, Math.round(top))));
  } catch {
    // 保存できない環境は覚えないだけ
  }
}

interface SfaListState {
  filters: SfaListFilters;
  page: number;
  pageSize: number;
  sortField: string;
  sortOrder: 'asc' | 'desc';
  filterName: string | null;
}

// 読めない・壊れている・保存できない環境 (プライベートモード等) では null を返し、既定の状態で開く
function loadListState(key?: string): SfaListState | null {
  if (!key) return null;
  try {
    const s = JSON.parse(sessionStorage.getItem(key) || 'null');
    if (!s || typeof s !== 'object' || !s.filters || typeof s.filters !== 'object') return null;
    return {
      filters: { ...DEFAULT_LIST_FILTERS, ...s.filters },
      page: Number(s.page) >= 1 ? Math.floor(Number(s.page)) : 1,
      pageSize: Number(s.pageSize) >= 1 ? Math.floor(Number(s.pageSize)) : 50,
      sortField: typeof s.sortField === 'string' && s.sortField ? s.sortField : DEFAULT_LIST_SORT.field,
      sortOrder: s.sortOrder === 'asc' ? 'asc' : 'desc',
      filterName: typeof s.filterName === 'string' && s.filterName ? s.filterName : null,
    };
  } catch {
    return null;
  }
}

// --------------------------------------------------
// API 呼び出し（入力不備は 400 で message が返る → Error として投げる）
// --------------------------------------------------
async function post<T = any>(name: string, body: any): Promise<T> {
  try {
    const res = await axios.post(ep(name), body);
    if (res.data?.status !== 'success') throw new Error(res.data?.message || res.data?.error || '処理に失敗しました');
    return res.data as T;
  } catch (e: any) {
    const msg = e?.response?.data?.message || e?.response?.data?.error || e?.message || '処理に失敗しました';
    throw new Error(msg);
  }
}

export const sfaStructureApi = {
  // 案件番号ミラーの検索 (番号は前方一致、案件名などは部分一致。ミラーに無い 12 桁はオンプレへ 1 件照会)
  searchLegacyAnken: (query: string, limit = 20) =>
    post<{ rows: LegacyAnkenRow[]; exact: boolean; looked_up: boolean; lookup_error?: string; message?: string; sync?: { finished_at?: string; watermark?: string } }>('search_legacy_anken', { query, limit }),
  list: (filters: SfaListFilters, page: number, pageSize: number, sortField: string, sortOrder: 'asc' | 'desc', userEmail?: string, viewLevel?: string) =>
    post<{ rows: SfaProjectRow[]; total: number }>('get_sfa_projects_v2', { filters, page, pageSize, sortField, sortOrder, userEmail, viewLevel }),
  detail: (projectId: string) => post<SfaProjectDetail>('get_sfa_project_v2', { project_id: projectId }),
  createProject: (data: any) => post<{ project_id: string }>('create_sfa_project_v2', data),
  updateProject: (projectId: string, data: any) => post('update_sfa_project_v2', { project_id: projectId, ...data }),
  // 契約日・決済日の入力で仕入フェーズが進んだ (戻った) ときは phase_changed=true と新しい phase が返る
  upsertContract: (projectId: string, contract: any, operatorId?: string) =>
    post<{ contract_id: number; phase?: string; phase_changed?: boolean }>('upsert_sfa_contract', { project_id: projectId, contract, operator_id: operatorId }),
  deleteContract: (projectId: string, contractId: number) => post('delete_sfa_contract', { project_id: projectId, contract_id: contractId }),
  upsertUnit: (projectId: string, unit: any, operatorId?: string) =>
    post<{ unit_id: number }>('upsert_sfa_unit', { project_id: projectId, unit, operator_id: operatorId }),
  deleteUnit: (projectId: string, unitId: number) => post('delete_sfa_unit', { project_id: projectId, unit_id: unitId }),
  setUnitCustomers: (projectId: string, unitId: number, customers: SfaUnitCustomer[]) =>
    post('set_sfa_unit_customers', { project_id: projectId, unit_id: unitId, customers }),
  changeUnitPhase: (projectId: string, unitId: number, body: { phase: string; lost_reason_category?: string; lost_reason_detail?: string; order_confirmed_date?: string }, operatorId?: string) =>
    post('change_sfa_unit_phase', { project_id: projectId, unit_id: unitId, ...body, operator_id: operatorId }),
  // 仕入契約がすべて失注になったときは lost_units（一緒に失注にした区画数）、すべて失注から戻したときは restored_units（元に戻した区画数）が返る
  changeContractPhase: (projectId: string, contractId: number, body: { phase: string; contract_date?: string; settlement_date?: string; lost_reason_category?: string; lost_reason_detail?: string }, operatorId?: string) =>
    post<{ lost_units?: number; restored_units?: number }>('change_sfa_contract_phase', { project_id: projectId, contract_id: contractId, ...body, operator_id: operatorId }),
  upsertParcels: (projectId: string, contractId: number, parcels: Partial<SfaParcel>[]) =>
    post('upsert_sfa_parcels', { project_id: projectId, contract_id: contractId, parcels }),
  addRevision: (projectId: string, contractId: number, body: { registered_date?: string; before_parcel_ids: number[]; after: { chiban: string; land_area: number | string; location?: string }[]; area_diff_reason?: string }, operatorId?: string) =>
    post<{ revision_id: number; rev_no: number; judge: string; area_before: number; area_after: number }>('add_sfa_parcel_revision', { project_id: projectId, contract_id: contractId, ...body, operator_id: operatorId }),
  searchTouki: (query: string) => post<{ rows: any[] }>('search_touki_candidates', { query }),
  masters: () => post<{ phases: any[]; lost_reasons: { reason_code: string; reason_name: string }[] }>('get_sfa_pipeline_masters', {}),
};

// --------------------------------------------------
// 一覧フック
// --------------------------------------------------
export function useSfaProjectsV2(opts: {
  viewLevel: string; userEmail?: string; ready: boolean;
  initialFilters?: SfaListFilters; initialSort?: SortConfig; initialFilterName?: string | null;
  storageKey?: string; // 指定するとタブ内に状態を覚え、次に開いたときに復元する
}) {
  const { viewLevel, userEmail, ready, storageKey } = opts;
  // 初期状態は、案件条件リストから適用した条件 → タブ内で覚えている状態 → 既定 の順
  const [init] = useState<SfaListState>(() => {
    if (opts.initialFilters) {
      const sort = opts.initialSort || DEFAULT_LIST_SORT;
      return { filters: opts.initialFilters, page: 1, pageSize: 50, sortField: sort.field, sortOrder: sort.order, filterName: opts.initialFilterName ?? null };
    }
    return loadListState(storageKey)
      || { filters: DEFAULT_LIST_FILTERS, page: 1, pageSize: 50, sortField: DEFAULT_LIST_SORT.field, sortOrder: DEFAULT_LIST_SORT.order, filterName: null };
  });
  const [filters, setFilters] = useState<SfaListFilters>(init.filters);
  const [page, setPage] = useState(init.page);
  const [pageSize, setPageSize] = useState(init.pageSize);
  const [sortField, setSortField] = useState(init.sortField);
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>(init.sortOrder);
  // 適用中の保存済み条件名 (一覧の「適用中」チップ)。条件と一緒に覚える
  const [appliedFilterName, setAppliedFilterName] = useState<string | null>(init.filterName);
  const [rows, setRows] = useState<SfaProjectRow[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const seq = useRef(0);

  const fetchRows = useCallback(async () => {
    if (!ready) return; // 権限 (閲覧範囲) が決まるまで取得しない
    const mySeq = ++seq.current;
    setLoading(true);
    setError(null);
    try {
      const data = await sfaStructureApi.list(filters, page, pageSize, sortField, sortOrder, userEmail, viewLevel);
      if (mySeq !== seq.current) return;
      // 覚えていたページが件数の減少 (詳細での編集で条件から外れた等) で範囲外になったら最終ページへ寄せる
      if (data.rows.length === 0 && data.total > 0 && page > 1) {
        setPage(Math.max(1, Math.ceil(data.total / pageSize)));
        return;
      }
      setRows(data.rows);
      setTotal(data.total);
    } catch (e: any) {
      if (mySeq !== seq.current) return;
      setError(e.message);
      setRows([]);
      setTotal(0);
    } finally {
      if (mySeq === seq.current) setLoading(false);
    }
  }, [filters, page, pageSize, sortField, sortOrder, userEmail, viewLevel, ready]);

  useEffect(() => { fetchRows(); }, [fetchRows]);

  // 状態が変わるたびにタブ内へ保存する (保存できない環境では覚えないだけで、一覧はそのまま使える)
  useEffect(() => {
    if (!storageKey) return;
    try {
      const state: SfaListState = { filters, page, pageSize, sortField, sortOrder, filterName: appliedFilterName };
      sessionStorage.setItem(storageKey, JSON.stringify(state));
    } catch {
      // 容量超過・保存禁止の環境は無視する
    }
  }, [storageKey, filters, page, pageSize, sortField, sortOrder, appliedFilterName]);

  const updateFilters = useCallback((patch: Partial<SfaListFilters>) => {
    setFilters((prev) => ({ ...prev, ...patch }));
    setPage(1);
  }, []);

  // 抽出条件ダイアログからの適用 (丸ごと置換。保存済み条件は旧キーだけのことがあるので既定とマージ)
  const applyFilters = useCallback((next: SfaListFilters) => {
    setFilters({ ...DEFAULT_LIST_FILTERS, ...next });
    setPage(1);
  }, []);

  const toggleSort = useCallback((field: string) => {
    setSortOrder((prev) => (sortField === field && prev === 'desc' ? 'asc' : 'desc'));
    setSortField(field);
  }, [sortField]);

  const setSort = useCallback((s: SortConfig) => { setSortField(s.field); setSortOrder(s.order); }, []);

  return {
    filters, updateFilters, applyFilters, page, setPage, pageSize, setPageSize, sortField, sortOrder, toggleSort, setSort,
    appliedFilterName, setAppliedFilterName, rows, total, loading, error, reload: fetchRows,
  };
}

// --------------------------------------------------
// 詳細フック
// --------------------------------------------------
export function useSfaProjectV2(projectId: string | undefined) {
  const [data, setData] = useState<SfaProjectDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const reload = useCallback(async (silent = false) => {
    if (!projectId) return;
    if (!silent) setLoading(true);
    try {
      setData(await sfaStructureApi.detail(projectId));
      setError(null);
    } catch (e: any) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => { reload(); }, [reload]);

  return { data, loading, error, reload };
}

// --------------------------------------------------
// 表示用の小さな整形
// --------------------------------------------------
export const fmtYen = (v: number | null | undefined): string => (v === null || v === undefined ? '-' : `${Math.round(Number(v)).toLocaleString()} 円`);
// 案件番号 (Hozenall NET で採番し工事管理へ引き継ぐ 12 桁の統一コード) を保存形に整える。
// 全角は半角に、ハイフン類と空白は落として 12 桁の数字にする。12 桁にならない文字列は旧データの名残として ASCII だけ残す
// (バックエンドの normalize_anken_no と同じ決まり)
export const normalizeSystemNumber = (v: string): string => {
  const s = (v || '').normalize('NFKC').replace(/[‐-―−－]/g, '-').replace(/[^\x21-\x7e]/g, '');
  const digits = s.replace(/[\s-]/g, '');
  return /^\d{12}$/.test(digits) ? digits : s;
};
// 12 桁を 2-4-6 桁のハイフン付きで表示する (帳票の表記)
export const formatSystemNumber = (n: string | null | undefined): string => (n && /^\d{12}$/.test(n) ? `${n.slice(0, 2)}-${n.slice(2, 6)}-${n.slice(6)}` : (n || ''));
export const normalizeSystemNumbers = (list: (string | null | undefined)[] | null | undefined): string[] => {
  const out: string[] = [];
  for (const x of list || []) { const s = normalizeSystemNumber(x || ''); if (s && !out.includes(s)) out.push(s); }
  return out;
};
export const SYSTEM_NUMBER_LABEL = '案件番号';
export const SYSTEM_NUMBER_HELP = '番号か案件名で検索して選びます（複数可）。工事管理システムに無い番号は登録できません';
export const fmtYm = (ym: string | null | undefined): string => {
  if (!ym) return '-';
  const [y, m] = ym.split('-');
  return y && m ? `${y}年${Number(m)}月` : ym;
};
export const fmtDate = (d: string | null | undefined): string => (d ? d.slice(0, 10).replace(/-/g, '/') : '-');
// 今日 (YYYY-MM-DD・端末の日付)。決済日の未来日判定と入力欄の上限に使う
export const todayIso = (): string => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};
// 決済日の表示（未来日は「決済予定」）
export const fmtSettlement = (d: string | null | undefined): string => (d ? `${d.slice(0, 10) > todayIso() ? '決済予定' : '決済'} ${fmtDate(d)}` : '決済日 未定');
// 担当者 (メイン担当・所有者・サポート担当者) の氏名・部署・支店を staff_id から補完する
export const STAFF_ROLES = [
  { id: 'main_staff_user_id', name: 'main_staff_name', dept: 'main_staff_department', branch: 'main_staff_branch', label: 'メイン担当' },
  { id: 'owner_id', name: 'owner_name', dept: 'owner_department', branch: 'owner_branch', label: '所有者' },
  { id: 'support_staff_id', name: 'support_staff_name', dept: 'support_staff_department', branch: 'support_staff_branch', label: 'サポート担当者' },
] as const;
export const fillStaffFields = (body: any, staffList: any[]) => {
  for (const r of STAFF_ROLES) {
    const id = body[r.id];
    if (id === undefined) continue;
    const s = staffList.find((x: any) => x.staff_id === id);
    body[r.name] = s ? s.staff_name : null; body[r.dept] = s ? s.department : null; body[r.branch] = s ? s.branch : null;
    if (!s) body[r.id] = null;
  }
  return body;
};
// 持分 「1/2」 の表記（両方あるときだけ）
export const fmtShare = (num: number | null | undefined, den: number | null | undefined): string => (num && den ? `${num}/${den}` : '');
// 持分の入力文字列を分子・分母へ。「1/2」「１／２」「2分の1」を受け付ける。空は null、読めなければ undefined
export const parseShare = (text: string): { num: number; den: number } | null | undefined => {
  const s = text.replace(/[０-９]/g, (ch) => String.fromCharCode(ch.charCodeAt(0) - 0xfee0)).replace(/／/g, '/').replace(/\s/g, '');
  if (s === '') return null;
  let m = s.match(/^(\d+)\/(\d+)$/);
  if (m) return { num: Number(m[1]), den: Number(m[2]) };
  m = s.match(/^(\d+)分の(\d+)$/);
  if (m) return { num: Number(m[2]), den: Number(m[1]) };
  return undefined;
};
// 顧客名に持分を添える（「佐々木 宏 (1/2)」）
export const customerLabel = (c: SfaUnitCustomer): string => {
  const share = fmtShare(c.share_numerator, c.share_denominator);
  return share ? `${c.display_name || ''} (${share})` : (c.display_name || '');
};
export const fmtArea = (v: number | null | undefined): string => (v === null || v === undefined ? '-' : `${Number(v).toFixed(2)} ㎡`);
// 区画が 1 つで仕入の無い案件は、画面上で区画を意識させない（建築受注・改修・仲介など）
export const isFlatProject = (d: SfaProjectDetail | null): boolean => !!d && d.contracts.length === 0 && d.units.length === 1;
// 案件の失注 = 仕入契約があり、すべて失注（仕入フェーズの代表が 7.失注）。失注日 (lost_at) は統合版で失注にした案件にだけ入るので判定に使わない
export const isProjectLost = (p: { contract_count?: number; purchase_phase?: string | null }): boolean => !!p.contract_count && p.purchase_phase === LOST_PHASE;
